import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { css, styled } from "@mui/system";
import { COLORS, FONT_FAMILIES, FONT_WEIGHTS, GAPS, RADII, SPACINGS } from "../../common/consts";
import { makeShouldForwardProps } from "../../common/utils/ui";
import Button, { ButtonVariants } from "../Display/Button";
import Text, { TextVariant } from "../Display/Text";
import { SettingsPanel, SettingsRow, SettingsRowSkeleton } from "../Settings";
import { BlockBone, TextBone } from "../Display/Skeletons";
import Toggle from "../DataInput/Toggle";
import Select from "../DataInput/Select";
import { getAddonStatus, getProductLicenseRules, updateProductLicenseRules } from "../../services/firebase";
import { AddonKey, AddonStatus, AddonStatusResult, ProductLicenseRules } from "../../types/addons";

/**
 * Licences per product.
 *
 * An organization can hold several subscriptions at once, so one pill nav
 * chooses the product and both panels below follow it: how many seats that
 * product has and who is using them, and which teams and profiles should get
 * it automatically on the next sync.
 */

const Wrap = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${GAPS.md};
`;

interface PillProps {
  active?: boolean;
  type?: "button";
  onClick?: () => void;
  children?: ReactNode;
}

const shouldForwardProp = makeShouldForwardProps<PillProps>(["active"]);

const Pill = styled("button", { shouldForwardProp })<PillProps>`
  height: 2.25rem;
  padding: 0 ${SPACINGS.lg};
  border-radius: ${RADII.full};
  border: none;
  background-color: transparent;
  color: var(--color-tabInactiveText, ${COLORS.tabInactiveText});
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--color-tabHoverBg, ${COLORS.tabHoverBg});
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }

  ${({ active }) =>
    active &&
    css`
      background-color: var(--color-backgroundBrandTab, ${COLORS.backgroundBrandTab});
    `}
`;

const Nav = styled("div")`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.xs};
  flex-wrap: wrap;
`;

const Panel = styled(SettingsPanel)`
  display: flex;
  flex-direction: column;
  gap: ${GAPS.sm};
`;

const Head = styled("div")`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${GAPS.md};
  flex-wrap: wrap;
`;

const Title = styled("div")`
  font-family: ${FONT_FAMILIES.titles};
  font-weight: ${FONT_WEIGHTS.semibold};
  font-size: 1rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const Seats = styled("div")`
  font-family: ${FONT_FAMILIES.titles};
  font-size: 1.5rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  line-height: 2.25rem;
  color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: ${GAPS.md};

  > :last-child:not(:first-child) {
    flex: 0 0 auto;
    margin-left: auto;
  }
`;

const Muted = styled(Text)`
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  margin: 0;
`;

interface Props {
  /** Team names available in the connected sales tool. */
  teams: string[];
  /** Profile or role names available in the connected sales tool. */
  roles: string[];
}

/**
 * The product picker and its two panels before the products and their
 * rules arrive: the pills, the seat count panel, and the assignment rules.
 */
export const ProductLicensesSkeleton: FC = () => (
  <Wrap>
    <Nav>
      <BlockBone width={112} height={36} radius={RADII.full} />
      <BlockBone width={96} height={36} radius={RADII.full} />
    </Nav>

    <Panel>
      <Head>
        <div style={{ flex: "1 1 12rem" }}>
          <TextBone width={160} size="1rem" />
          <TextBone width="82%" size="0.875rem" />
        </div>
        <div>
          <TextBone width={72} size="1.5rem" />
          <TextBone width={112} size="0.875rem" />
        </div>
      </Head>
      <TextBone width="88%" size="0.875rem" />
    </Panel>

    <Panel>
      <div>
        <TextBone width={190} size="1rem" />
        <TextBone width="84%" size="0.875rem" />
      </div>
      <SettingsRowSkeleton labelWidth={260} captionWidth="52%" control="toggle" />
      <div style={{ maxWidth: 520 }}>
        <BlockBone height={40} />
      </div>
      <div style={{ maxWidth: 520 }}>
        <BlockBone height={40} />
      </div>
      <Row>
        <TextBone width={230} size="0.875rem" />
        <BlockBone width={112} height={36} radius={RADII.full} />
      </Row>
    </Panel>
  </Wrap>
);

const ProductLicenses: FC<Props> = ({ teams, roles }) => {
  const [status, setStatus] = useState<AddonStatusResult | null>(null);
  const [rules, setRules] = useState<ProductLicenseRules | null>(null);
  const [selected, setSelected] = useState<AddonKey | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([getAddonStatus(), getProductLicenseRules()]);
      setStatus(s);
      setRules(r);
      const first = s.addons.find((a) => a.enabled) || s.addons[0];
      if (first) setSelected(first.key);
    } catch (e: any) {
      setMessage(e?.message || "Could not load product licences.");
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const available = useMemo(() => (status?.addons || []).filter((a) => a.enabled), [status]);
  const addon: AddonStatus | undefined = useMemo(() => available.find((a) => a.key === selected), [available, selected]);
  const rule = (selected && rules?.[selected]) || { teams: [], roles: [], autoSync: false };

  if ((!status || !rules) && message) return <Muted variant={TextVariant.sm}>{message}</Muted>;

  if (!status || !rules) return <ProductLicensesSkeleton />;

  if (available.length === 0) {
    return <Muted variant={TextVariant.sm}>No products are on yet. Subscribe under Add-ons and they will show up here.</Muted>;
  }

  const save = async (patch: Partial<{ teams: string[]; roles: string[]; autoSync: boolean }>) => {
    if (!selected) return;
    setSaving(true);
    setMessage(null);
    try {
      setRules(await updateProductLicenseRules({ addon: selected, ...rule, ...patch }));
    } catch (e: any) {
      setMessage(e?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrap>
      <Nav>
        {available.map((a) => (
          <Pill key={a.key} type="button" active={a.key === selected} onClick={() => setSelected(a.key)}>
            {a.name}
          </Pill>
        ))}
      </Nav>

      <Panel>
        <Head>
          <div>
            <Title>{addon?.name}</Title>
            <Muted variant={TextVariant.sm}>{addon?.description}</Muted>
          </div>
          <div>
            <Seats>
              {addon?.seatLimit === null ? `${addon?.assigned.length || 0}` : `${addon?.assigned.length || 0} / ${addon?.seatLimit ?? 0}`}
            </Seats>
            <Muted variant={TextVariant.sm}>{addon?.seatLimit === null ? "users, no seat limit" : "seats assigned"}</Muted>
          </div>
        </Head>
        <Muted variant={TextVariant.sm}>
          {addon?.source === "subscription"
            ? "Billed on your subscription. Assign people on the Users page, or let the rules below do it."
            : "Included for this organization. Assign people on the Users page, or let the rules below do it."}
        </Muted>
      </Panel>

      <Panel>
        <div>
          <Title>Automatic assignment</Title>
          <Muted variant={TextVariant.sm}>
            Anyone in these teams or profiles is given {addon?.name.toLowerCase()} on the next sync, and loses it when they move away or go inactive.
          </Muted>
        </div>

        <SettingsRow
          label={`Assign ${addon?.name.toLowerCase()} automatically`}
          caption="Off means you assign this product by hand on the Users page."
          control={<Toggle checked={rule.autoSync} saving={saving} onChange={(v) => save({ autoSync: v })} />}
        />

        <div style={{ maxWidth: 520 }}>
          <Select
            multiple
            fullWidth
            label="Teams"
            value={rule.teams as any}
            options={teams.map((t) => ({ value: t, label: t }))}
            renderValue={(v: any) => (Array.isArray(v) && v.length ? v.join(", ") : "Any team")}
            onChange={(e: any) => save({ teams: (e?.target?.value || []) as string[] })}
          />
        </div>
        <div style={{ maxWidth: 520 }}>
          <Select
            multiple
            fullWidth
            label="Profiles"
            value={rule.roles as any}
            options={roles.map((r) => ({ value: r, label: r }))}
            renderValue={(v: any) => (Array.isArray(v) && v.length ? v.join(", ") : "Any profile")}
            onChange={(e: any) => save({ roles: (e?.target?.value || []) as string[] })}
          />
        </div>

        <Row>
          <Muted variant={TextVariant.sm}>{message || (saving ? "Saving" : "Changes save as you make them.")}</Muted>
          <Button variant={ButtonVariants.light} type="button" disabled={saving} onClick={() => save({ teams: [], roles: [], autoSync: false })}>
            Clear rules
          </Button>
        </Row>
      </Panel>
    </Wrap>
  );
};

export default ProductLicenses;
